import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, X, RefreshCw, Save, ArrowUp, ArrowDown, Upload } from 'lucide-react';
import {
    knowledgePrioritiesService,
    KnowledgePriority,
    KnowledgePriorityInput,
} from '../services/KnowledgeRulesService';
import { adminService } from '../services/AdminService';

interface OrgOption { id: number; name: string; }

// Languages the system supports for search. The `code` MUST be the runtime
// `client_language` value the backend produces (see map_language_keys in
// catax_ai/config/languages.py) — it lowercases input, so Mexican Spanish is
// `es-mx`, not `es-MX`. fetch_priorities matches this code case-sensitively, so
// using the wrong casing would mean a rule never applies.
//
// `*` is the language wildcard: a rule scoped to `*` applies to EVERY language
// (mirrors the `*` catalog wildcard). fetch_priorities always includes `*` in
// its match set.
const LANGUAGE_OPTIONS: { code: string; label: string }[] = [
    { code: '*', label: 'All languages' },
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'es-mx', label: 'Español (México)' },
    { code: 'es-cl', label: 'Español (Chile)' },
    { code: 'es-ec', label: 'Español (Ecuador)' },
    { code: 'pt', label: 'Português' },
];

const emptyPriority = (): KnowledgePriorityInput => ({
    catalog: '*',
    language: 'es-mx',
    organization_id: null,
    term: '',
    meanings: [''],
    note: '',
    enabled: true,
});

const KnowledgeRulesTab: React.FC = () => {
    const [priorities, setPriorities] = useState<KnowledgePriority[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filterCatalog, setFilterCatalog] = useState('');
    const [filterLanguage, setFilterLanguage] = useState('');
    const [filterTerm, setFilterTerm] = useState('');
    const [filterScope, setFilterScope] = useState<'all' | 'global' | string>('all'); // 'all' | 'global' | orgId

    const [orgs, setOrgs] = useState<OrgOption[]>([]);
    const orgName = useCallback(
        (id?: number | null) => (id == null ? 'Global' : (orgs.find(o => o.id === id)?.name || `Org ${id}`)),
        [orgs],
    );

    const [editing, setEditing] = useState<KnowledgePriority | null>(null);
    const [form, setForm] = useState<KnowledgePriorityInput | null>(null);
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importing, setImporting] = useState(false);
    const [importMsg, setImportMsg] = useState<string | null>(null);

    const fetchPriorities = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await knowledgePrioritiesService.list({
                catalog: filterCatalog || undefined,
                language: filterLanguage || undefined,
                term: filterTerm || undefined,
                global_only: filterScope === 'global' ? true : undefined,
                organization_id: filterScope !== 'all' && filterScope !== 'global' ? Number(filterScope) : undefined,
            });
            setPriorities(res.data.priorities || []);
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to load knowledge priorities');
        } finally {
            setLoading(false);
        }
    }, [filterCatalog, filterLanguage, filterTerm, filterScope]);

    useEffect(() => { fetchPriorities(); }, [fetchPriorities]);

    useEffect(() => {
        adminService.getOrganizations()
            .then(res => setOrgs((res.data.organizations || []).map((o: any) => ({ id: o.id, name: o.name }))))
            .catch(() => { /* org list is optional for the table */ });
    }, []);

    const openCreate = () => { setEditing(null); setForm(emptyPriority()); };
    const openEdit = (p: KnowledgePriority) => {
        setEditing(p);
        const { id, created_by, created_at, updated_at, ...input } = p;
        setForm({ ...emptyPriority(), ...input, meanings: input.meanings?.length ? [...input.meanings] : [''] });
    };
    const closeForm = () => { setForm(null); setEditing(null); };

    const setField = (key: keyof KnowledgePriorityInput, value: any) =>
        setForm(prev => (prev ? { ...prev, [key]: value } : prev));

    // --- ordered meanings editing ---
    const setMeaning = (idx: number, value: string) =>
        setForm(prev => prev ? { ...prev, meanings: prev.meanings.map((m, i) => i === idx ? value : m) } : prev);
    const addMeaning = () =>
        setForm(prev => prev ? { ...prev, meanings: [...prev.meanings, ''] } : prev);
    const removeMeaning = (idx: number) =>
        setForm(prev => prev ? { ...prev, meanings: prev.meanings.filter((_, i) => i !== idx) } : prev);
    const moveMeaning = (idx: number, dir: -1 | 1) =>
        setForm(prev => {
            if (!prev) return prev;
            const next = [...prev.meanings];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return prev;
            [next[idx], next[j]] = [next[j], next[idx]];
            return { ...prev, meanings: next };
        });

    const save = async () => {
        if (!form) return;
        const meanings = form.meanings.map(m => m.trim()).filter(Boolean);
        if (!form.term.trim()) { setError('Term is required.'); return; }
        if (meanings.length === 0) { setError('Add at least one meaning (ranked catalog keyword).'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = { ...form, term: form.term.trim(), meanings };
            if (editing) {
                await knowledgePrioritiesService.update(editing.id, payload);
            } else {
                await knowledgePrioritiesService.create(payload);
            }
            closeForm();
            await fetchPriorities();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to save priority');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p: KnowledgePriority) => {
        if (!window.confirm(`Delete the priority for "${p.term}"?`)) return;
        try {
            await knowledgePrioritiesService.remove(p.id);
            await fetchPriorities();
        } catch (e: any) {
            setError(e?.response?.data?.detail || 'Failed to delete priority');
        }
    };

    const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;
        setImporting(true); setError(null); setImportMsg(null);
        try {
            const { data } = await knowledgePrioritiesService.importYaml(file);
            const tail = data.errors?.length ? `, ${data.errors.length} error(s)` : '';
            setImportMsg(`Loaded "${file.name}": ${data.created} created, ${data.updated} updated, ${data.skipped} skipped${tail}.`);
            if (data.errors?.length) setError(data.errors.slice(0, 5).join(' · '));
            await fetchPriorities();
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to import YAML');
        } finally {
            setImporting(false);
        }
    };

    const meanings = useMemo(() => form?.meanings ?? [], [form]);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Catalog Knowledge Dictionary</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Expert-curated term priorities compiled into the OE search agent's prompt. Each
                        entry maps a requested <em>term</em> to an <strong>ordered list of meanings</strong>{' '}
                        (catalog part-name keywords) — rank&nbsp;1 is the strongest match — which controls
                        how "similar parts" ambiguity resolves. Changes take effect on the next search.
                        Use catalog <code className="px-1 bg-gray-100 rounded">*</code> to apply to every catalog.
                    </p>
                </div>
                <div className="flex gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".yaml,.yml,text/yaml,application/x-yaml"
                        className="hidden"
                        onChange={onImportFile}
                    />
                    <button onClick={() => fileInputRef.current?.click()} disabled={importing} title="Bulk-load a taxonomy YAML (seed_taxonomy.yaml format)" className="flex items-center px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        <Upload className="w-4 h-4 mr-2" /> {importing ? 'Importing…' : 'Upload YAML'}
                    </button>
                    <button onClick={fetchPriorities} className="flex items-center px-3 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50">
                        <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </button>
                    <button onClick={openCreate} className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" /> Add priority
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <input value={filterCatalog} onChange={e => setFilterCatalog(e.target.value)} placeholder="Filter catalog" className="px-3 py-2 text-sm border rounded-lg" />
                <select value={filterLanguage} onChange={e => setFilterLanguage(e.target.value)} className="px-3 py-2 text-sm border rounded-lg">
                    <option value="">Any language</option>
                    {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label} ({l.code})</option>)}
                </select>
                <input value={filterTerm} onChange={e => setFilterTerm(e.target.value)} placeholder="Filter term" className="px-3 py-2 text-sm border rounded-lg" />
                <select value={filterScope} onChange={e => setFilterScope(e.target.value)} className="px-3 py-2 text-sm border rounded-lg">
                    <option value="all">All scopes</option>
                    <option value="global">Global only</option>
                    {orgs.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                </select>
            </div>

            {importMsg && <div className="px-4 py-3 bg-green-50 text-green-700 text-sm rounded-lg">{importMsg}</div>}
            {error && <div className="px-4 py-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-gray-500">
                            <th className="px-4 py-2 font-medium">Catalog</th>
                            <th className="px-4 py-2 font-medium">Lang</th>
                            <th className="px-4 py-2 font-medium">Scope</th>
                            <th className="px-4 py-2 font-medium">Term</th>
                            <th className="px-4 py-2 font-medium">Ranked meanings</th>
                            <th className="px-4 py-2 font-medium">Enabled</th>
                            <th className="px-4 py-2 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {loading && (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                        )}
                        {!loading && priorities.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No priorities yet — add one to start tuning the agent.</td></tr>
                        )}
                        {!loading && priorities.map(p => (
                            <tr key={p.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 font-mono text-xs">{p.catalog}</td>
                                <td className="px-4 py-2">{p.language}</td>
                                <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${p.organization_id == null ? 'bg-gray-100 text-gray-600' : 'bg-purple-50 text-purple-700'}`}>
                                        {orgName(p.organization_id)}
                                    </span>
                                </td>
                                <td className="px-4 py-2 font-medium text-gray-900">{p.term}</td>
                                <td className="px-4 py-2 text-gray-700">
                                    <div className="flex flex-wrap gap-1">
                                        {(p.meanings || []).map((m, i) => (
                                            <span key={i} className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                                                <span className="text-blue-400 mr-1">{i + 1}.</span>{m}
                                            </span>
                                        ))}
                                    </div>
                                    {p.note && <div className="text-xs text-gray-400 mt-1">{p.note}</div>}
                                </td>
                                <td className="px-4 py-2">{p.enabled ? '✓' : '—'}</td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    <button onClick={() => openEdit(p)} className="p-1.5 text-gray-500 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                                    <button onClick={() => remove(p)} className="p-1.5 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create / edit modal */}
            {form && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <h3 className="font-semibold text-gray-900">{editing ? 'Edit priority' : 'Add priority'}</h3>
                            <button onClick={closeForm} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <label className="text-sm">
                                    <span className="text-gray-600">Catalog</span>
                                    <input value={form.catalog} onChange={e => setField('catalog', e.target.value)} className="mt-1 w-full px-3 py-2 border rounded-lg" placeholder="* (all) or catalog name" />
                                </label>
                                <label className="text-sm">
                                    <span className="text-gray-600">Language</span>
                                    <select value={form.language} onChange={e => setField('language', e.target.value)} className="mt-1 w-full px-3 py-2 border rounded-lg">
                                        {!LANGUAGE_OPTIONS.some(l => l.code === form.language) && form.language && (
                                            <option value={form.language}>{form.language}</option>
                                        )}
                                        {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label} ({l.code})</option>)}
                                    </select>
                                </label>
                            </div>
                            <label className="text-sm block">
                                <span className="text-gray-600">Organization scope</span>
                                <select
                                    value={form.organization_id == null ? '' : String(form.organization_id)}
                                    onChange={e => setField('organization_id', e.target.value === '' ? null : Number(e.target.value))}
                                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="">Global (all organizations)</option>
                                    {orgs.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                                </select>
                                <span className="text-xs text-gray-400">An org priority overrides a matching global one for that org only.</span>
                            </label>
                            <label className="text-sm block">
                                <span className="text-gray-600">Term (requested / local)</span>
                                <input value={form.term} onChange={e => setField('term', e.target.value)} className="mt-1 w-full px-3 py-2 border rounded-lg" placeholder="bumper" />
                            </label>

                            <div className="text-sm">
                                <span className="text-gray-600">Ranked meanings</span>
                                <p className="text-xs text-gray-400 mb-2">Catalog name keywords, highest priority first. Rank 1 is the strongest match.</p>
                                <div className="space-y-2">
                                    {meanings.map((m, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <span className="w-5 text-right text-xs text-gray-400">{idx + 1}.</span>
                                            <input
                                                value={m}
                                                onChange={e => setMeaning(idx, e.target.value)}
                                                className="flex-1 px-3 py-2 border rounded-lg"
                                                placeholder={idx === 0 ? 'assembly' : 'Face Bumper'}
                                            />
                                            <button type="button" onClick={() => moveMeaning(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                                            <button type="button" onClick={() => moveMeaning(idx, 1)} disabled={idx === meanings.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                                            <button type="button" onClick={() => removeMeaning(idx)} disabled={meanings.length === 1} className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" onClick={addMeaning} className="mt-2 flex items-center px-2 py-1 text-xs border rounded-lg text-gray-700 hover:bg-gray-50">
                                    <Plus className="w-3 h-3 mr-1" /> Add meaning
                                </button>
                            </div>

                            <label className="text-sm block">
                                <span className="text-gray-600">Note (optional)</span>
                                <textarea value={form.note || ''} onChange={e => setField('note', e.target.value)} className="mt-1 w-full px-3 py-2 border rounded-lg" rows={2} placeholder="Why / caveats for this term." />
                            </label>

                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.enabled} onChange={e => setField('enabled', e.target.checked)} />
                                <span className="text-gray-700">Enabled</span>
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t">
                            <button onClick={closeForm} className="px-4 py-2 text-sm border rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                            <button onClick={save} disabled={saving} className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KnowledgeRulesTab;
